import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { fetchCurrentMenu, fetchCurrentMenuDetails } from 'menus/actions';

import { fetchLocationInventory } from 'locations/actions/locationInventory';

import usePrevious from 'base/hooks/usePrevious';
import { fetchCartLocationThrottledTimes } from 'locations/actions/locationThrottledTimes';

import isDineInApplicationVariant from 'utils/isDineInApplicationVariant';
import { ALL_ONLINE_ORDERING_FULFILLMENT_METHODS } from 'cart/constants/fulfillmentOptions';

// if we refactor MenuContainer and CartContainer to be functional components, all this logic can just live in hooks and be instantiated in one of those components. until then, we keep this logic here

const MenuManager = ({
  dispatch,
  menuId,
  selectedDate,
  selectedTime,
  selectedMethod,
  selectedAsap,
  error,
  selectedLocation,
  hasAlreadyFetchedForLocation
}) => {
  const prevSelectedFulfillmentOptions = usePrevious({
    selectedDate,
    selectedTime,
    selectedMethod,
    selectedAsap,
    menuId
  });

  useEffect(() => {
    if (error || !selectedLocation) {
      return;
    }

    const dateDidChange =
      prevSelectedFulfillmentOptions.current.selectedDate !== selectedDate;
    const timeDidChange =
      prevSelectedFulfillmentOptions.current.selectedTime !== selectedTime;
    const methodDidChange =
      prevSelectedFulfillmentOptions.current.selectedMethod !== selectedMethod;
    const asapDidChange =
      prevSelectedFulfillmentOptions.current.selectedAsap !== selectedAsap;
    const menuDidChange =
      menuId && prevSelectedFulfillmentOptions.current.menuId !== menuId;

    if (
      dateDidChange ||
      timeDidChange ||
      methodDidChange ||
      asapDidChange ||
      menuDidChange ||
      !menuId
    ) {
      dispatch(fetchCurrentMenu());
    }
  }, [
    selectedDate,
    selectedTime,
    selectedMethod,
    selectedAsap,
    menuId,
    error,
    selectedLocation
  ]);

  useEffect(() => {
    if (menuId) {
      dispatch(fetchCurrentMenuDetails());
    }
  }, [menuId]);

  useEffect(() => {
    const shouldFetchLocationInventory =
      !isDineInApplicationVariant() &&
      menuId &&
      selectedLocation &&
      !hasAlreadyFetchedForLocation;

    if (shouldFetchLocationInventory) {
      dispatch(fetchLocationInventory(selectedLocation));
    }
  }, [menuId, selectedLocation, hasAlreadyFetchedForLocation]);

  useEffect(() => {
    if (menuId) {
      dispatch(fetchCartLocationThrottledTimes());
    }
  }, [menuId]);

  return null;
};

MenuManager.propTypes = {
  dispatch: PropTypes.func.isRequired,
  menuId: PropTypes.number,
  error: PropTypes.string,
  hasAlreadyFetchedForLocation: PropTypes.bool,
  selectedLocation: PropTypes.number,
  selectedDate: PropTypes.string,
  selectedTime: PropTypes.string,
  selectedMethod: PropTypes.oneOf([
    ...ALL_ONLINE_ORDERING_FULFILLMENT_METHODS,
    ''
  ]),
  selectedAsap: PropTypes.bool
};

MenuManager.defaultProps = {
  menuId: null,
  error: '',
  hasAlreadyFetchedForLocation: false,
  selectedLocation: null,
  selectedDate: '',
  selectedTime: '',
  selectedMethod: '',
  selectedAsap: false
};

const mapStateToProps = ({ menu, cart, locationInventory }) => {
  const menuId = menu.id;
  const error = menu.error;

  const hasAlreadyFetchedForLocation = locationInventory.byLocationId.hasOwnProperty(
    cart.online_ordering_location
  );

  return {
    menuId,
    error,
    hasAlreadyFetchedForLocation,
    selectedLocation: cart.online_ordering_location,
    selectedDate: cart.fulfillment_date,
    selectedTime: cart.fulfillment_time,
    selectedMethod: cart.online_ordering_fulfillment_method,
    selectedAsap: cart.requested_fulfillment_is_asap
  };
};

export default withRouter(connect(mapStateToProps)(MenuManager));
